import PaginatedFlatTable from "../../pagination/PaginatedFlatTable";
import GetSanctions from "../queries/GetSanctions";
import { formattedDateTime } from "../../../util/date";
import { toast } from "react-toastify";
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useMutation } from "@apollo/client";
import { CREATE_SANCTION, UPDATE_SANCTION, DELETE_SANCTION } from "../mutations/Sanction";
import { confirmDelete } from "../../common/confirmDelete";
import useThemeColors from "../../organization/portal/useThemeColors";
import { alphaFromHex } from "../../../util/colors";
import useTranslation from "../../../i18n/useTranslation";
import { getLocalizedGraphQLError } from "../../../util/graphqlError";

export default function Sanctions({ season, sanctions, games }) {
    const colors = useThemeColors();
    const { t, language } = useTranslation();

    const {
        sanctions: sanctionsList,
        setSanctions,
        sanctionsPage,
        sanctionsTotalPages,
        setSanctionsPage,
        setSanctionsTotalPages,
    } = sanctions;

    const [createSanction] = useMutation(CREATE_SANCTION);
    const [updateSanction] = useMutation(UPDATE_SANCTION);
    const [deleteSanction] = useMutation(DELETE_SANCTION);
    const loadedSeasonIdRef = useRef(null);

    const [showModal, setShowModal] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [formData, setFormData] = useState({
        sanction: null,
        profileId: "",
        teamId: "",
        gameId: "",
        type: "",
        reason: "",
        notes: "",
        gamesSuspended: "",
    });

    const [teamsForGame, setTeamsForGame] = useState([]);
    const [profilesForTeam, setProfilesForTeam] = useState([]);

    const [getSanctions] = GetSanctions();
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const timezone = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone, []);

    useEffect(() => {
        if (!showModal) return;
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = prevOverflow;
        };
    }, [showModal]);

    const closeModal = useCallback(() => {
        if (submitting) return;
        setShowModal(false);
        setEditingId(null);
    }, [submitting]);

    useEffect(() => {
        if (!showModal) return;
        const onKeyDown = (e) => {
            if (e.key === "Escape") closeModal();
        };
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [showModal, closeModal]);

    const ui = useMemo(() => {
        return {
            actionBtn: {
                width: 38,
                height: 38,
                borderRadius: 12,
                border: "1px solid rgba(0,0,0,0.10)",
                background: "#fff",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                lineHeight: 1,
            },
            actionBtnDanger: {
                border: "1px solid rgba(220,53,69,0.35)",
                background: "rgba(220,53,69,0.06)",
                color: "#dc3545",
            },
            actionBtnEdit: {
                border: "1px solid rgba(0,0,0,0.10)",
                background: "rgba(0,0,0,0.02)",
                color: "#14151a",
            },

            modalBackdrop: {
                backgroundColor: "rgba(0,0,0,0.55)",
            },
            modalDialog: {
                maxWidth: 720,
            },
            modalContent: {
                borderRadius: 18,
                overflow: "hidden",
                border: "1px solid rgba(0,0,0,0.08)",
                boxShadow: "0 18px 44px rgba(0,0,0,0.18)",
            },
            modalHeader: {
                padding: "12px 14px",
                background: `linear-gradient(
                    135deg,
                    ${alphaFromHex(colors.primary, 0.50)},
                    ${alphaFromHex(colors.primary, 0.00)}
                )`,
                borderBottom: "1px solid rgba(0,0,0,0.06)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
            },
            modalTitleWrap: {
                display: "flex",
                alignItems: "center",
                gap: 10,
                minWidth: 0,
            },
            modalBadge: {
                fontSize: 11.5,
                fontWeight: 900,
                letterSpacing: "0.03em",
                padding: "6px 10px",
                borderRadius: 999,
                background: colors.primary,
                border: "1px solid rgba(0,0,0,0.10)",
                color: colors.textOnPrimary,
                whiteSpace: "nowrap",
            },
            modalTitle: {
                margin: 0,
                fontWeight: 900,
                fontSize: 14,
                color: colors.textOnPrimary,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
            },
            modalSub: {
                margin: 0,
                fontSize: 12.5,
                fontWeight: 700,
                color: colors.textOnPrimary,
            },
            closeBtn: {
                width: 38,
                height: 38,
                borderRadius: 12,
                border: "1px solid rgba(0,0,0,0.10)",
                background: colors.primary,
                color: colors.textOnPrimary,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                lineHeight: 1,
                flex: "0 0 auto",
            },
            modalBody: {
                padding: 14,
                background: "#fff",
                maxHeight: "calc(100vh - 220px)",
                overflowY: "auto",
                WebkitOverflowScrolling: "touch",
            },
            fieldLabel: {
                fontWeight: 900,
                fontSize: 14,
                color: "rgba(20,21,26,0.72)",
                marginBottom: 6,
            },
            modalFooter: {
                padding: "12px 14px",
                borderTop: "1px solid rgba(0,0,0,0.06)",
                background: "rgba(0,0,0,0.01)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 10,
            },
            btnGhost: {
                borderRadius: 12,
                padding: "10px 12px",
                fontWeight: 900,
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                border: "1px solid rgba(0,0,0,0.12)",
                background: "#fff",
                color: "#14151a",
                cursor: "pointer",
                lineHeight: 1,
            },
            btnPrimaryModal: {
                borderRadius: 12,
                padding: "10px 12px",
                fontWeight: 900,
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                border: "1px solid rgba(0,0,0,0.10)",
                background: colors.primary,
                color: colors.textOnPrimary,
                cursor: "pointer",
                lineHeight: 1,
            },
            formGrid: {
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 12,
            },
            formRowFull: { gridColumn: "1 / -1" },
        };
    }, [colors.primary, colors.textOnPrimary]);

    const getSanctionsHandler = async (page) => {
        if (!season?.id) return;
        setLoading(true);
        try {
            const res = await getSanctions({
                variables: { seasonId: season.id, page, pageSize: 20 },
                fetchPolicy: "network-only",
            });

            const payload = res.data?.sanctionsBySeason;
            setSanctions(payload?.objects ?? []);
            setSanctionsPage(payload?.page ?? page);
            setSanctionsTotalPages(payload?.pages ?? 1);
        } catch (err) {
            toast.error(getLocalizedGraphQLError(err, t, "sanctions.toasts.loadError"), {
                autoClose: 3000,
                toastId: "sanctions_query_error",
                position: "top-center",
                closeOnClick: true,
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (loadedSeasonIdRef.current === season?.id) return;
        loadedSeasonIdRef.current = season?.id;
        if (season?.id) getSanctionsHandler(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [season?.id]);

    const handleGameChange = (e) => {
        const gameId = e.target.value;
        const selectedGame = games.find((g) => g.game?.id === gameId);
        if (!selectedGame) return;

        const teamOptions = [
            {
                id: selectedGame.game?.homeTeam?.id,
                name: selectedGame.game?.homeTeam?.name,
                players: selectedGame.game?.homeTeam?.players,
            },
            {
                id: selectedGame.game?.visitorTeam?.id,
                name: selectedGame.game?.visitorTeam?.name,
                players: selectedGame.game?.visitorTeam?.players,
            },
        ];

        setFormData((prev) => ({ ...prev, gameId, teamId: "", profileId: "" }));
        setTeamsForGame(teamOptions);
        setProfilesForTeam([]);
    };

    const handleTeamChange = (e) => {
        const teamId = e.target.value;
        const team = teamsForGame.find((t) => String(t.id) === String(teamId));
        setFormData((prev) => ({ ...prev, teamId, profileId: "" }));
        setProfilesForTeam(team?.players || []);
    };

    const handleInput = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleEdit = (sanction) => {
        setEditingId(sanction.id);
        setFormData({
            sanction: sanction,
            profileId: sanction.profile?.id || "",
            teamId: sanction.team?.id || "",
            gameId: sanction.game?.id || "",
            type: (sanction.type || "").toLowerCase(),
            reason: sanction.reason || "",
            notes: sanction.notes || "",
            gamesSuspended: sanction.gamesSuspended ?? "",
        });

        const selectedGame = games.find((g) => g.game?.id === sanction.game?.id);
        if (selectedGame) {
            const teamOptions = [
                {
                    id: selectedGame.game?.homeTeam?.id,
                    name: selectedGame.game?.homeTeam?.name,
                    players: selectedGame.game?.homeTeam?.players,
                },
                {
                    id: selectedGame.game?.visitorTeam?.id,
                    name: selectedGame.game?.visitorTeam?.name,
                    players: selectedGame.game?.visitorTeam?.players,
                },
            ];
            setTeamsForGame(teamOptions);

            const team = teamOptions.find((t) => String(t.id) === String(sanction.team?.id));
            setProfilesForTeam(team?.players || []);
        } else {
            setTeamsForGame([]);
            setProfilesForTeam([]);
        }

        setShowModal(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);

        const variables = { input: formData };

        try {
            if (editingId) {
                await updateSanction({ variables: { id: editingId, ...variables } });
                toast.success(t("sanctions.toasts.updateSuccess"));
            } else {
                await createSanction({ variables });
                toast.success(t("sanctions.toasts.createSuccess"));
            }

            closeModal();
            await getSanctionsHandler(sanctionsPage);
        } catch (err) {
            toast.error(getLocalizedGraphQLError(err, t, "sanctions.toasts.genericError"));
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = (id) => {
        confirmDelete({
            title: t("sanctions.deleteConfirm.title"),
            message: t("sanctions.deleteConfirm.message"),
            confirmLabel: t("sanctions.deleteConfirm.confirmLabel"),
            cancelLabel: t("sanctions.deleteConfirm.cancelLabel"),
            danger: true,
            onConfirm: async () => {
                try {
                    await deleteSanction({ variables: { id } });
                    toast.success(t("sanctions.toasts.deleted"), {
                        autoClose: 3000,
                        toastId: "sanction_deleted",
                        position: "top-center",
                        closeOnClick: true,
                    });
                    await getSanctionsHandler(sanctionsPage);
                } catch {
                    toast.error(t("sanctions.toasts.deleteError"), {
                        autoClose: 3000,
                        toastId: "sanction_delete_error",
                        position: "top-center",
                        closeOnClick: true,
                    });
                }
            },
        });
    };

    return (
        <>
            <div id="kbola-sanctions">
                <PaginatedFlatTable
                    title={t("sanctions.title")}
                    page={sanctionsPage}
                    pages={sanctionsTotalPages}
                    items={sanctionsList}
                    loading={loading}
                    onPageChange={(page) => getSanctionsHandler(page)}
                    headers={[
                        t("sanctions.headers.player"),
                        t("sanctions.headers.game"),
                        t("sanctions.headers.type"),
                        t("sanctions.headers.missedGames"),
                        t("sanctions.headers.date"),
                        t("sanctions.headers.actions"),
                    ]}
                    renderRow={(sanction, index) => (
                        <tr key={sanction.id || index}>
                            <td>{sanction.profile?.fullName || t("sanctions.placeholders.empty")}</td>
                            <td>{sanction.game?.name || t("sanctions.placeholders.empty")}</td>
                            <td className="text-capitalize">
                                {t(`sanctions.sanctionTypes.${(sanction.type || "").toLowerCase()}`)}
                            </td>
                            <td className="text-center">
                                {sanction.gamesSuspended || t("sanctions.placeholders.empty")}
                            </td>
                            <td>
                                {formattedDateTime(
                                    sanction.issuedAt,
                                    timezone,
                                    language || "en",
                                    "cccc, LLLL d, yyyy"
                                )}
                            </td>
                            <td className="text-right">
                                <div style={{ display: "inline-flex", gap: 8 }}>
                                    <button
                                        type="button"
                                        style={{ ...ui.actionBtn, ...ui.actionBtnEdit, opacity: submitting ? 0.65 : 1 }}
                                        onClick={() => handleEdit(sanction)}
                                        title={t("sanctions.actions.edit")}
                                        aria-label={t("sanctions.actions.edit")}
                                        disabled={submitting}
                                    >
                                        <i className="fas fa-edit" />
                                    </button>

                                    <button
                                        type="button"
                                        style={{ ...ui.actionBtn, ...ui.actionBtnDanger, opacity: submitting ? 0.65 : 1 }}
                                        onClick={() => handleDelete(sanction.id)}
                                        title={t("sanctions.actions.delete")}
                                        aria-label={t("sanctions.actions.delete")}
                                        disabled={submitting}
                                    >
                                        <i className="fas fa-trash" />
                                    </button>
                                </div>
                            </td>
                        </tr>
                    )}
                />

                {showModal && (
                    <div
                        id="kbola-sanction-modal"
                        className="modal fade show d-block"
                        tabIndex="-1"
                        role="dialog"
                        style={ui.modalBackdrop}
                        onMouseDown={(e) => {
                            if (e.target === e.currentTarget) closeModal();
                        }}
                    >
                        <div className="modal-dialog" role="document" style={ui.modalDialog}>
                            <div
                                className="modal-content"
                                style={ui.modalContent}
                                onMouseDown={(e) => e.stopPropagation()}
                            >
                                <div style={ui.modalHeader}>
                                    <div style={ui.modalTitleWrap}>
                                        <span style={ui.modalBadge}>
                                            {editingId ? t("sanctions.badge.edit") : t("sanctions.badge.new")}
                                        </span>
                                        <div style={{ minWidth: 0 }}>
                                            <h5 style={ui.modalTitle}>
                                                {editingId
                                                    ? t("sanctions.modal.editTitle")
                                                    : t("sanctions.modal.addTitle")}
                                            </h5>
                                            <p style={ui.modalSub}>
                                                {editingId
                                                    ? t("sanctions.modal.editSubtitle")
                                                    : t("sanctions.modal.addSubtitle", {
                                                        season: season?.name || t("sanctions.values.thisSeason"),
                                                    })}
                                            </p>
                                        </div>
                                    </div>

                                    <button
                                        type="button"
                                        style={ui.closeBtn}
                                        onClick={closeModal}
                                        title={t("sanctions.actions.close")}
                                        aria-label={t("sanctions.actions.close")}
                                        disabled={submitting}
                                    >
                                        <i className="fas fa-times" />
                                    </button>
                                </div>

                                <form onSubmit={handleSubmit}>
                                    <div className="modal-body" style={ui.modalBody}>
                                        <div style={ui.formGrid} className="kbola-sanction-grid">
                                            {!editingId ? (
                                                <>
                                                    <div>
                                                        <label style={ui.fieldLabel}>
                                                            {t("sanctions.fields.game")}
                                                        </label>
                                                        <select
                                                            name="gameId"
                                                            className="form-control kbola-input"
                                                            value={formData.gameId}
                                                            onChange={handleGameChange}
                                                            required
                                                            disabled={submitting}
                                                        >
                                                            <option value="">
                                                                {t("sanctions.options.selectGame")}
                                                            </option>
                                                            {games.map((g) => (
                                                                <option key={g.game?.id} value={g.game?.id}>
                                                                    {g.title}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>

                                                    <div>
                                                        <label style={ui.fieldLabel}>
                                                            {t("sanctions.fields.team")}
                                                        </label>
                                                        <select
                                                            name="teamId"
                                                            className="form-control kbola-input"
                                                            value={formData.teamId}
                                                            onChange={handleTeamChange}
                                                            required
                                                            disabled={submitting || !formData.gameId}
                                                        >
                                                            <option value="">
                                                                {t("sanctions.options.selectTeam")}
                                                            </option>
                                                            {teamsForGame.map((t) => (
                                                                <option key={t.id} value={t.id}>
                                                                    {t.name}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>

                                                    <div style={ui.formRowFull}>
                                                        <label style={ui.fieldLabel}>
                                                            {t("sanctions.fields.player")}
                                                        </label>
                                                        <select
                                                            name="profileId"
                                                            className="form-control kbola-input"
                                                            value={formData.profileId}
                                                            onChange={handleInput}
                                                            required
                                                            disabled={submitting || !formData.teamId}
                                                        >
                                                            <option value="">
                                                                {t("sanctions.options.selectPlayer")}
                                                            </option>
                                                            {profilesForTeam.map((p) => (
                                                                <option key={p.playerId} value={p.playerId}>
                                                                    {p.fullName}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                </>
                                            ) : (
                                                <>
                                                    <div>
                                                        <label style={ui.fieldLabel}>
                                                            {t("sanctions.fields.game")}:{" "}
                                                        </label>
                                                        <label style={ui.fieldLabel}>
                                                            {formData.sanction.game.name}
                                                        </label>
                                                    </div>

                                                    <div>
                                                        <label style={ui.fieldLabel}>
                                                            {t("sanctions.fields.team")}:{" "}
                                                        </label>
                                                        <label style={ui.fieldLabel}>
                                                            {formData.sanction.team.name}
                                                        </label>
                                                    </div>

                                                    <div style={ui.formRowFull}>
                                                        <label style={ui.fieldLabel}>
                                                            {t("sanctions.fields.player")}:{" "}
                                                        </label>
                                                        <label style={ui.fieldLabel}>
                                                            {formData.sanction.profile.fullName}
                                                        </label>
                                                    </div>
                                                </>
                                            )}

                                            <div>
                                                <label style={ui.fieldLabel}>
                                                    {t("sanctions.fields.type")}
                                                </label>
                                                <select
                                                    name="type"
                                                    className="form-control kbola-input"
                                                    value={(formData.type || "").toLowerCase()}
                                                    onChange={handleInput}
                                                    required
                                                    disabled={submitting}
                                                >
                                                    <option value="">
                                                        {t("sanctions.options.selectType")}
                                                    </option>
                                                    <option value="suspension">
                                                        {t("sanctions.sanctionTypes.suspension")}
                                                    </option>
                                                    <option value="fine">
                                                        {t("sanctions.sanctionTypes.fine")}
                                                    </option>
                                                </select>
                                            </div>

                                            <div>
                                                <label style={ui.fieldLabel}>
                                                    {t("sanctions.fields.gamesSuspended")}
                                                </label>
                                                <input
                                                    type="number"
                                                    name="gamesSuspended"
                                                    className="form-control kbola-input"
                                                    value={formData.gamesSuspended}
                                                    onChange={handleInput}
                                                    disabled={submitting}
                                                    placeholder={t("sanctions.placeholders.gamesSuspended")}
                                                    min={0}
                                                />
                                            </div>

                                            <div style={ui.formRowFull}>
                                                <label style={ui.fieldLabel}>
                                                    {t("sanctions.fields.reason")}
                                                </label>
                                                <input
                                                    name="reason"
                                                    className="form-control kbola-input"
                                                    value={formData.reason}
                                                    onChange={handleInput}
                                                    required
                                                    disabled={submitting}
                                                    placeholder={t("sanctions.placeholders.reason")}
                                                />
                                            </div>

                                            <div style={ui.formRowFull}>
                                                <label style={ui.fieldLabel}>
                                                    {t("sanctions.fields.notes")}
                                                </label>
                                                <textarea
                                                    name="notes"
                                                    className="form-control kbola-input"
                                                    value={formData.notes}
                                                    onChange={handleInput}
                                                    disabled={submitting}
                                                    rows={4}
                                                    placeholder={t("sanctions.placeholders.notes")}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="modal-footer" style={ui.modalFooter}>
                                        <button
                                            type="button"
                                            style={{ ...ui.btnGhost, opacity: submitting ? 0.7 : 1 }}
                                            onClick={closeModal}
                                            disabled={submitting}
                                        >
                                            {t("sanctions.actions.cancel")}
                                        </button>

                                        <button type="submit" style={ui.btnPrimaryModal} disabled={submitting}>
                                            {submitting ? (
                                                <>
                                                    <span
                                                        className="spinner-border spinner-border-sm"
                                                        role="status"
                                                        aria-hidden="true"
                                                    />
                                                    {t("sanctions.actions.saving")}
                                                </>
                                            ) : (
                                                <>
                                                    <i className={`fas ${editingId ? "fa-save" : "fa-plus"}`} />
                                                    {editingId
                                                        ? t("sanctions.actions.update")
                                                        : t("sanctions.actions.add")}
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </form>

                                <style>{`
                  #kbola-sanction-modal .kbola-input{
                    border-radius: 12px !important;
                    border: 1px solid rgba(0,0,0,0.10) !important;
                    font-weight: 800 !important;
                    height: auto !important;
                  }
                  #kbola-sanction-modal .kbola-input:focus{
                    outline: none !important;
                    box-shadow: 0 0 0 0.2rem rgba(var(--kbs-primary-rgb, 0,173,255), 0.18) !important;
                    border-color: rgba(var(--kbs-primary-rgb, 0,173,255), 0.65) !important;
                  }
                  #kbola-sanction-modal textarea.kbola-input{
                    resize: vertical;
                  }

                  @media (max-width: 820px){
                    #kbola-sanction-modal .kbola-sanction-grid{
                      grid-template-columns: 1fr !important;
                    }
                  }
                `}</style>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
}
